import { useCallback, useRef } from 'react'
import audioService from '../services/audioService'

/**
 * Универсальный хук для управления звуками через глобальный AudioService
 * 
 * Преимущества:
 * - Все звуки предзагружены при старте приложения
 * - Мгновенное воспроизведение без задержек
 * - Возможность воспроизводить один звук много раз одновременно
 * - Лучшая производительность при частом воспроизведении
 * - Меньше проблем с autoplay policy браузеров
 * - Точное управление громкостью через GainNode
 * 
 * @param {Object} soundMapping - Маппинг локальных ключей на глобальные { localKey: 'globalKey' }
 * @returns {Object} - { playSound }
 * 
 * @example
 * const { playSound } = useAudioManager({
 *   click: 'buttonClick',
 *   win: 'winV3'
 * })
 * playSound('click') // Воспроизведет buttonClick из глобального audioService
 */
const useAudioManager = (soundMapping = {}) => {
  // Храним актуальный mapping в ref, чтобы playSound/stopSound были стабильными
  // и не пересоздавались при каждом рендере (литерал soundMapping создаётся заново).
  // Это критично для useEffect-ов, у которых cleanup останавливает зацикленные звуки.
  const mappingRef = useRef(soundMapping)
  mappingRef.current = soundMapping

  /**
   * Воспроизведение звука через глобальный audioService
   * @param {string} localKey - Локальный ключ звука (из soundMapping)
   * @param {number} volumeOverride - Опциональная перезапись громкости (0.0 - 1.0)
   * @param {Object} options - Дополнительные опции воспроизведения
   */
  const playSound = useCallback((localKey, volumeOverride = null, options = {}) => {
    // Получаем глобальный ключ из актуального маппинга через ref
    const globalKey = mappingRef.current?.[localKey]
    
    if (!globalKey) {
      console.warn(`⚠️ Sound mapping not found for local key: ${localKey}`)
      return
    }

    // Воспроизводим через глобальный audioService
    audioService.play(globalKey, volumeOverride, options)
  }, [])

  /**
   * Остановка конкретного звука
   * @param {string} localKey - Локальный ключ звука (из soundMapping)
   */
  const stopSound = useCallback((localKey) => {
    // Получаем глобальный ключ из актуального маппинга через ref
    const globalKey = mappingRef.current?.[localKey]
    
    if (!globalKey) {
      console.warn(`⚠️ Sound mapping not found for local key: ${localKey}`)
      return
    }

    // Останавливаем через глобальный audioService
    audioService.stop(globalKey)
  }, [])

  /**
   * Остановка всех звуков
   */
  const stopAllSounds = useCallback(() => {
    audioService.stopAll()
  }, [])

  return {
    playSound,
    stopSound,
    stopAllSounds
  }
}

export default useAudioManager
